import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { STATS } from "@/data/content";

export default function About() {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: imgRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="about" data-testid="about-section" className="relative py-32 lg:py-44 px-6 lg:px-24">
      <div className="max-w-[1600px] mx-auto">
        <div className="grid lg:grid-cols-12 gap-14 lg:gap-20 items-start">
          <div className="lg:col-span-6">
            <p className="font-mono text-[10px] tracking-[0.35em] text-goldw uppercase mb-6">Chapter 04 — About AMAA</p>
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-ivory leading-[1.08]"
              data-testid="about-headline"
            >
              BUILDING DREAMS.<br />
              <span className="italic text-goldw">CRAFTING LEGACY.</span>
            </motion.h2>
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.2 }}
              className="mt-10 space-y-6 max-w-xl"
            >
              <p className="text-sm sm:text-base text-zinc2 leading-relaxed">
                AMAA Constructions is a detail-focused design and build partner serving the
                Dallas–Fort Worth Metroplex. We combine disciplined planning, premium
                craftsmanship, honest communication and sophisticated design — so the
                process feels as considered as the result.
              </p>
              <p className="text-sm sm:text-base text-zinc2 leading-relaxed">
                Every project, from a single bathroom to a ground-up custom home, is
                coordinated end to end: design development, material selection, construction
                management and a final walkthrough measured against your vision.
              </p>
            </motion.div>

            <div className="mt-14 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/8 border border-white/8" data-testid="about-stats">
              {STATS.map((s) => (
                <div key={s.label} className="bg-ink px-5 py-7">
                  <div className="font-serif text-3xl sm:text-4xl text-goldw font-light">{s.value}</div>
                  <div className="mt-2 font-mono text-[9px] tracking-[0.18em] uppercase text-zinc3 leading-relaxed">{s.label}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-6 lg:sticky lg:top-28">
            <div ref={imgRef} className="relative overflow-hidden aspect-[4/5] max-h-[80vh]">
              <motion.img
                style={{ y, scale: 1.15 }}
                src="/images/about.webp"
                alt="Architectural blueprints and material samples for a luxury Dallas home"
                loading="lazy"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 border border-white/5 pointer-events-none" />
            </div>
            <div className="mt-4 flex items-center gap-4">
              <span className="h-px w-12 bg-goldw/40" />
              <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-zinc3">
                Planning, precision &amp; material — Farmers Branch, TX
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
